import "./bootstrap";
// import "./mapbox-form";

// ---------------------
// UTILIDADES
// ---------------------
const MAPBOX_SCRIPT_SRC = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";
const MAPBOX_STYLE_HREF = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
const MAPBOX_STYLE_ID = "dollo-mapbox-styles";

const ensureMapboxStyles = () => {
    if (document.getElementById(MAPBOX_STYLE_ID)) {
        return;
    }

    const link = document.createElement("link");
    link.id = MAPBOX_STYLE_ID;
    link.rel = "stylesheet";
    link.href = MAPBOX_STYLE_HREF;
    document.head.appendChild(link);
};

const ensureMapboxReady = async () => {
    if (!window.MAPBOX_TOKEN) {
        return null;
    }

    ensureMapboxStyles();

    if (!window.__dolloMapboxPromise) {
        window.__dolloMapboxPromise = new Promise((resolve, reject) => {
            if (window.mapboxgl) {
                resolve(window.mapboxgl);
                return;
            }

            const existingScript = document.querySelector(
                `script[data-dollo-mapbox="true"]`,
            );

            if (existingScript) {
                existingScript.addEventListener("load", () =>
                    resolve(window.mapboxgl),
                );
                existingScript.addEventListener("error", reject);
                return;
            }

            const script = document.createElement("script");
            script.src = MAPBOX_SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.dataset.dolloMapbox = "true";
            script.onload = () => resolve(window.mapboxgl);
            script.onerror = reject;
            document.head.appendChild(script);
        }).catch((error) => {
            window.__dolloMapboxPromise = null;
            throw error;
        });
    }

    const mapboxgl = await window.__dolloMapboxPromise;
    mapboxgl.accessToken = window.MAPBOX_TOKEN;

    return mapboxgl;
};

const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
};

const throttle = (fn, wait = 200) => {
    let lastTime = 0;
    return function (...args) {
        const now = Date.now();
        if (now - lastTime >= wait) {
            lastTime = now;
            fn.apply(this, args);
        }
    };
};

// ---------------------
// FUNCIONES GEOJSON
// ---------------------
function createGeoJSONCircle([lng, lat], radiusInMeters = 200, points = 32) {
    const coords = [];
    const distanceX =
        radiusInMeters / (111320 * Math.cos((lat * Math.PI) / 180));
    const distanceY = radiusInMeters / 110574;

    for (let i = 0; i < points; i++) {
        const theta = (i / points) * 2 * Math.PI;
        coords.push([
            lng + distanceX * Math.cos(theta),
            lat + distanceY * Math.sin(theta),
        ]);
    }
    coords.push(coords[0]);

    return {
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [coords] },
        properties: {},
    };
}

// ---------------------
// MAPA
// ---------------------
const initDolloMainMap = async () => {
    const mapContainer = document.getElementById("mapBoxForm");
    if (!mapContainer) {
        return;
    }

    if (window.__dolloMapInitialized) {
        return;
    }

    const mapboxgl = await ensureMapboxReady();
    if (!mapboxgl) {
        return;
    }

    if (window.__dolloMapInitialized) {
        return;
    }

    window.__dolloMapInitialized = true;

    const registeredListeners = [];
    const registerWindowEvent = (event, handler, options) => {
        window.addEventListener(event, handler, options);
        registeredListeners.push({ event, handler, options });
    };

    const cleanup = () => {
        registeredListeners.forEach(({ event, handler, options }) => {
            window.removeEventListener(event, handler, options);
        });
        registeredListeners.length = 0;
        window.__dolloMapInitialized = false;
    };

    registerWindowEvent("beforeunload", cleanup);
    const mapState = {
        showAddForm: false,
        showEditForm: false,
        currentDelivery: null,
    };

    const canAddMarker = () => {
        if (!mapState.showAddForm && !mapState.showEditForm) return false;

        // Solo permitir en presencial o ambos
        if (
            mapState.showEditForm &&
            (mapState.currentDelivery === "1" ||
                mapState.currentDelivery === "3")
        )
            return true;
        if (
            mapState.showAddForm &&
            (mapState.currentDelivery === "1" ||
                mapState.currentDelivery === "3")
        )
            return true;

        return false;
    };

    // parse raw from DOM
    let rawLocations = [];
    try {
        rawLocations = JSON.parse(mapContainer.dataset.locations || "[]");
    } catch (e) {
        console.error("No se pudo parsear locations:", e);
    }

    // Evitar que se creen varios mapas en el mismo div
    if (mapContainer.hasChildNodes()) {
        mapContainer.innerHTML = "";
    }

    // Normalizador para tener siempre la misma forma interna
    const normalizeLocations = (posts) => {
        if (!Array.isArray(posts)) return [];
        return posts.map((p, i) => {
            const lat =
                (p.location && (p.location.latitude ?? p.location.lat)) ??
                p.latitude ??
                p.lat ??
                null;
            const lng =
                (p.location && (p.location.longitude ?? p.location.lng)) ??
                p.longitude ??
                p.lng ??
                null;

            return {
                id: p.id ?? p.post_id ?? i + 1,
                post: p.post ?? p.title ?? "",
                latitude: lat != null ? parseFloat(lat) : null,
                longitude: lng != null ? parseFloat(lng) : null,
                index: i + 1,
            };
        });
    };

    // Inicializamos locations en forma normalizada
    let locations = normalizeLocations(rawLocations);

    const overviewCenter = [-3.573, 39.2279];
    const overviewZoom = 3.9;
    const overviewPanLockFactor = 0.2;

    const map = new mapboxgl.Map({
        container: "mapBoxForm",
        style: "mapbox://styles/mapbox/streets-v12",
        center: overviewCenter,
        zoom: overviewZoom,
        maxZoom: 15,
        minZoom: overviewZoom,
    });

    map.on("remove", cleanup);
    // Forzar resize cuando se alterna la visibilidad del mapa
    const ensureResize = () => {
        if (!mapContainer) return;
        let tries = 0;
        const attempt = () => {
            if (!map) return;
            const { offsetWidth, offsetHeight } = mapContainer;
            if (offsetWidth > 0 && offsetHeight > 0) {
                map.resize();
            } else if (tries < 10) {
                tries += 1;
                setTimeout(attempt, 120);
            }
        };
        attempt();
    };

    let overviewCenterLockBounds = null;
    let isClampingOverviewCenter = false;

    const updateOverviewCenterLockBounds = () => {
        const bounds = map.getBounds();
        const center = bounds.getCenter();
        const lngSpan =
            (bounds.getEast() - bounds.getWest()) * overviewPanLockFactor;
        const latSpan =
            (bounds.getNorth() - bounds.getSouth()) * overviewPanLockFactor;

        overviewCenterLockBounds = {
            minLng: center.lng - lngSpan / 2,
            maxLng: center.lng + lngSpan / 2,
            minLat: center.lat - latSpan / 2,
            maxLat: center.lat + latSpan / 2,
        };
    };

    const clampOverviewCenterIfNeeded = () => {
        if (
            isClampingOverviewCenter ||
            !overviewCenterLockBounds ||
            map.getZoom() > overviewZoom + 0.02
        ) {
            return;
        }

        const center = map.getCenter();
        const clampedLng = Math.min(
            overviewCenterLockBounds.maxLng,
            Math.max(overviewCenterLockBounds.minLng, center.lng),
        );
        const clampedLat = Math.min(
            overviewCenterLockBounds.maxLat,
            Math.max(overviewCenterLockBounds.minLat, center.lat),
        );

        if (
            Math.abs(clampedLng - center.lng) < 0.000001 &&
            Math.abs(clampedLat - center.lat) < 0.000001
        ) {
            return;
        }

        isClampingOverviewCenter = true;
        map.jumpTo({
            center: [clampedLng, clampedLat],
            zoom: map.getZoom(),
            bearing: map.getBearing(),
            pitch: map.getPitch(),
            essential: true,
        });
        isClampingOverviewCenter = false;
    };

    const relockOverviewIfNeeded = () => {
        if (map.getZoom() > overviewZoom + 0.02) {
            return;
        }

        map.jumpTo({
            center: overviewCenter,
            zoom: overviewZoom,
            essential: true,
        });
        updateOverviewCenterLockBounds();
    };

    const scheduleResize = () => {
        ensureResize();
        setTimeout(() => {
            ensureResize();
            relockOverviewIfNeeded();
        }, 200);
        setTimeout(() => {
            ensureResize();
            relockOverviewIfNeeded();
        }, 450);
    };
    registerWindowEvent("toggle-map", scheduleResize);
    registerWindowEvent("map-visible", scheduleResize);
    map.on("dragend", clampOverviewCenterIfNeeded);
    map.on("zoomend", clampOverviewCenterIfNeeded);
    map.on("moveend", clampOverviewCenterIfNeeded);

    const applyOverviewState = (duration = 0) => {
        const overviewOptions = {
            center: overviewCenter,
            zoom: overviewZoom,
            essential: true,
        };

        map.setMinZoom(overviewZoom);
        map.setMaxBounds(null);

        if (duration > 0) {
            map.easeTo({
                ...overviewOptions,
                duration,
            });
            map.once("moveend", () => {
                updateOverviewCenterLockBounds();
            });
            return;
        }

        map.jumpTo(overviewOptions);
        updateOverviewCenterLockBounds();
    };

    const currentMapViewConfig = () => {
        const center = map.getCenter();
        const bounds = map.getBounds();

        return {
            overviewCenter: [
                Number(center.lng.toFixed(4)),
                Number(center.lat.toFixed(4)),
            ],
            overviewZoom: Number(map.getZoom().toFixed(2)),
            resetBounds: [
                [
                    Number(bounds.getWest().toFixed(4)),
                    Number(bounds.getSouth().toFixed(4)),
                ],
                [
                    Number(bounds.getEast().toFixed(4)),
                    Number(bounds.getNorth().toFixed(4)),
                ],
            ],
        };
    };

    // Cuando el mapa carga, ajustamos a la vista general
    map.on("load", () => {
        applyOverviewState(0);
        scheduleResize();

        class ResetMapControl {
            onAdd(map) {
                this._map = map;
                this._btn = document.createElement("button");
                this._btn.className = "mapboxgl-ctrl-icon mapboxgl-ctrl-reset";
                this._btn.type = "button";
                this._btn.title = "Volver a la vista general";
                this._btn.innerHTML = "📍";
                this._btn.onclick = () => {
                    applyOverviewState(1500);
                };
                const container = document.createElement("div");
                container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
                container.appendChild(this._btn);
                return container;
            }
            onRemove() {
                this._btn.remove();
                this._map = undefined;
            }
        }
        map.addControl(new ResetMapControl(), "top-right");

        // ---------------------
        // VARIABLES DE ESTADO
        // ---------------------
        let summaryVisible = false;
        let currentMarker = null;
        let tempMarker = null;
        let reverseGeocodeController = null;
        const detailLayerId = "detail-circles-layer";
        const detailSourceId = "detail-circles-source";
        const summaryLayers = [
            "summary-circles",
            "summary-labels",
            "summary-unclustered",
            "summary-unclustered-labels",
        ];

        // 🔹 Flag para diferenciar click vs zoom manual
        let forceDetailMode = false;

        // ---------------------
        // DEBOUNCE CANCELABLE
        // ---------------------
        function makeCancelableDebounce(fn, wait = 200) {
            let timeout = null;
            const wrapper = (...args) => {
                if (timeout) clearTimeout(timeout);
                timeout = setTimeout(() => {
                    timeout = null;
                    fn(...args);
                }, wait);
            };
            wrapper.cancel = () => {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = null;
                }
            };
            return wrapper;
        }

        const debouncedClearPinnedPost = makeCancelableDebounce(() => {
            window.dispatchEvent(new Event("dollo-map-post-cleared"));
            Livewire.dispatch("clearPinnedPost");
        }, 250);

        // ---------------------
        // INITIALIZE DETAIL LAYER (always exists)
        // ---------------------
        function initDetailLayer() {
            if (map.getSource(detailSourceId)) return;

            map.addSource(detailSourceId, {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] },
            });

            map.addLayer({
                id: detailLayerId,
                type: "fill",
                source: detailSourceId,
                paint: {
                    "fill-color": "#f08",
                    "fill-opacity": 0.65,
                    "fill-outline-color": "#fff",
                },
            });

            // Click en layer de detalle
            map.on("click", detailLayerId, (e) => {
                const feature = e.features?.[0];
                if (!feature) return;
                const loc = locations.find(
                    (l) => String(l.id) === String(feature.properties.id),
                );
                if (!loc) return;

                debouncedClearPinnedPost.cancel();
                currentMarker?.remove();
                currentMarker = new mapboxgl.Marker({ color: "#E65100" })
                    .setLngLat([loc.longitude, loc.latitude])
                    .addTo(map);

                window.dispatchEvent(
                    new CustomEvent("dollo-map-post-clicked", {
                        detail: { postId: loc.id },
                    }),
                );
                Livewire.dispatch("scroll-to-post", { postId: loc.id });
            });

            // ---------------------
            // BORRAR currentMarker si click fuera de detailLayer
            // ---------------------
            map.on("click", (e) => {
                // Obtenemos todos los features debajo del click
                const features = map.queryRenderedFeatures(e.point, {
                    layers: [detailLayerId],
                });

                // Si no hay features debajo del click, significa que se hizo click fuera
                if (!features.length && currentMarker) {
                    currentMarker.remove();
                    currentMarker = null;

                    // Opcional: notificar a Livewire que se deseleccionó
                    window.dispatchEvent(new Event("dollo-map-post-cleared"));
                    Livewire.dispatch("clearPinnedPost");
                }
            });

            map.on(
                "mouseenter",
                detailLayerId,
                () => (map.getCanvas().style.cursor = "pointer"),
            );
            map.on(
                "mouseleave",
                detailLayerId,
                () => (map.getCanvas().style.cursor = ""),
            );

            // ---------------------
            // ESCUCHAR EVENTO LIVEWIRE PARA ACTUALIZAR MARKERS
            // ---------------------
            Livewire.on("update-map-markers", (payload) => {
                const posts = Array.isArray(payload)
                    ? payload
                    : (payload?.posts ?? []);

                const normalized = normalizeLocations(posts);
                locations = normalized.map((item, idx) => ({
                    ...item,
                    index: idx + 1,
                }));

                const features = locations
                    .filter((l) => l.latitude != null && l.longitude != null)
                    .map((l) => ({
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: [
                                parseFloat(l.longitude),
                                parseFloat(l.latitude),
                            ],
                        },
                        properties: { id: l.id, index: l.index },
                    }));

                // Actualizar summary
                if (map.getSource("summary-circles-source")) {
                    map.getSource("summary-circles-source").setData({
                        type: "FeatureCollection",
                        features,
                    });
                } else {
                    map.addSource("summary-circles-source", {
                        type: "geojson",
                        data: { type: "FeatureCollection", features },
                        cluster: true,
                        clusterRadius: 30,
                        clusterMaxZoom: 9,
                    });
                    updateSummaryFromLocations();
                }

                // --------------------------
                // Actualizar detailLayer si estamos en zoom final
                // --------------------------
                if (!summaryVisible) {
                    showDetailCircles(locations);
                }
            });
        }

        initDetailLayer(); // always create layer

        // ---------------------
        // CLICK GLOBAL
        // ---------------------
        map.on("click", async (e) => {
            if (!canAddMarker()) return;

            const lng = e.lngLat.lng;
            const lat = e.lngLat.lat;

            // Crear o mover marker del usuario (azul)
            if (!formMarker) {
                formMarker = new mapboxgl.Marker({ color: "#3FB1CE" })
                    .setLngLat([lng, lat])
                    .addTo(map);
            } else {
                formMarker.setLngLat([lng, lat]);
            }

            // Reverse geocoding con Mapbox para obtener dirección completa
            let address = "";
            let city = "";
            let province = "";
            let region = "";
            let postalCode = "";

            try {
                if (reverseGeocodeController) {
                    reverseGeocodeController.abort();
                }
                reverseGeocodeController = new AbortController();

                const response = await fetch(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`,
                    { signal: reverseGeocodeController.signal },
                );
                const data = await response.json();
                if (data.features && data.features.length > 0) {
                    const place = data.features[0];
                    address = place.place_name || "";

                    place.context?.forEach((c) => {
                        if (c.id.startsWith("place")) city = c.text;
                        if (c.id.startsWith("region")) region = c.text;
                        if (c.id.startsWith("postcode")) postalCode = c.text;
                        if (
                            c.id.startsWith("district") ||
                            c.id.startsWith("province")
                        ) {
                            province = c.text;
                        }
                    });
                }
                reverseGeocodeController = null;
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error("Error reverse geocoding:", err);
                }
                reverseGeocodeController = null;
            }

            // Enviar todos los datos a Livewire
            Livewire.dispatch("updateLocationFromMap", {
                lat,
                lng,
                address,
                city,
                province,
                region,
                postalCode,
            });
        });

        // ---------------------
        // FUNCIONES SUMMARY
        // ---------------------
        function updateSummaryFromLocations() {
            const features = locations
                .filter((loc) => loc.latitude != null && loc.longitude != null)
                .map((loc) => ({
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [
                            parseFloat(loc.longitude),
                            parseFloat(loc.latitude),
                        ],
                    },
                    properties: { id: loc.id },
                }));

            if (!map.getSource("summary-circles-source")) {
                map.addSource("summary-circles-source", {
                    type: "geojson",
                    data: { type: "FeatureCollection", features },
                    cluster: true,
                    clusterRadius: 30,
                    clusterMaxZoom: 9, // 🔹 clusters solo hasta zoom 9
                });

                // Cluster circles
                map.addLayer({
                    id: "summary-circles",
                    type: "circle",
                    source: "summary-circles-source",
                    filter: ["has", "point_count"],
                    paint: {
                        "circle-color": "#ff5722",
                        "circle-radius": 12,
                        "circle-stroke-color": "#fff",
                        "circle-stroke-width": 2,
                    },
                });

                // Cluster labels (conteo)
                map.addLayer({
                    id: "summary-labels",
                    type: "symbol",
                    source: "summary-circles-source",
                    filter: ["has", "point_count"],
                    layout: {
                        "text-field": ["get", "point_count_abbreviated"],
                        "text-font": ["Open Sans Bold"],
                        "text-size": 12,
                        "text-anchor": "center",
                    },
                    paint: { "text-color": "#fff" },
                });

                // Single points
                map.addLayer({
                    id: "summary-unclustered",
                    type: "circle",
                    source: "summary-circles-source",
                    filter: ["!", ["has", "point_count"]],
                    paint: {
                        "circle-color": "#ff5722",
                        "circle-radius": 12,
                        "circle-stroke-color": "#fff",
                        "circle-stroke-width": 2,
                    },
                });

                map.addLayer({
                    id: "summary-unclustered-labels",
                    type: "symbol",
                    source: "summary-circles-source",
                    filter: ["!", ["has", "point_count"]],
                    layout: {
                        "text-field": "1",
                        "text-font": ["Open Sans Bold"],
                        "text-size": 12,
                        "text-anchor": "center",
                    },
                    paint: { "text-color": "#fff" },
                });

                ["summary-circles", "summary-unclustered"].forEach((layer) => {
                    map.on(
                        "mouseenter",
                        layer,
                        () => (map.getCanvas().style.cursor = "pointer"),
                    );
                    map.on(
                        "mouseleave",
                        layer,
                        () => (map.getCanvas().style.cursor = ""),
                    );
                });

                // Click cluster → flyin hasta posts individuales
                map.on("click", "summary-circles", (e) => {
                    const features = map.queryRenderedFeatures(e.point, {
                        layers: ["summary-circles"],
                    });
                    if (!features || !features[0]) return;

                    const clusterId = features[0].properties.cluster_id;

                    map.getSource("summary-circles-source").getClusterLeaves(
                        clusterId,
                        Infinity,
                        0,
                        (err, leaves) => {
                            if (err) return;
                            if (!leaves.length) return;

                            const coords = leaves.map((l) => [
                                l.geometry.coordinates[0],
                                l.geometry.coordinates[1],
                            ]);

                            let minX = coords[0][0],
                                minY = coords[0][1],
                                maxX = coords[0][0],
                                maxY = coords[0][1];

                            coords.forEach(([lng, lat]) => {
                                if (lng < minX) minX = lng;
                                if (lat < minY) minY = lat;
                                if (lng > maxX) maxX = lng;
                                if (lat > maxY) maxY = lat;
                            });

                            const bounds = [
                                [minX, minY],
                                [maxX, maxY],
                            ];

                            // 🔹 Forzamos detalle
                            forceDetailMode = true;

                            // Fit bounds con animación
                            map.fitBounds(bounds, {
                                padding: 80,
                                duration: 1500,
                                essential: true,
                            });

                            // Mostrar detalle inmediatamente
                            summaryVisible = false;
                            summaryLayers.forEach((layer) =>
                                map.setLayoutProperty(
                                    layer,
                                    "visibility",
                                    "none",
                                ),
                            );
                            showDetailCircles(locations);

                            // 🔹 Resetear forceDetailMode al terminar animación
                            const onMoveEnd = () => {
                                forceDetailMode = false;
                                syncMapPresentation({
                                    respectForceDetail: false,
                                });
                                map.off("moveend", onMoveEnd);
                            };
                            map.on("moveend", onMoveEnd);
                        },
                    );
                });

                // Click punto individual
                map.on("click", "summary-unclustered", (e) => {
                    const feature = e.features[0];
                    const loc = locations.find(
                        (l) => l.id === feature.properties.id,
                    );
                    if (!loc) return;

                    map.easeTo({
                        center: [loc.longitude, loc.latitude],
                        zoom: 11,
                        duration: 1500,
                        curve: 1.5,
                    });

                    map.once("moveend", () => showDetailCircles([loc]));
                });
            } else {
                map.getSource("summary-circles-source").setData({
                    type: "FeatureCollection",
                    features,
                });
            }
        }

        // ---------------------
        // DETAIL
        // ---------------------
        function showDetailCircles(postsInCity, radiusMeters = 200) {
            // Si hay un form abierto, siempre ocultamos/limpiamos los círculos de detalle
            if (mapState.showAddForm || mapState.showEditForm) {
                // limpiar datos
                map.getSource(detailSourceId)?.setData({
                    type: "FeatureCollection",
                    features: [],
                });
                // y ocultar la capa (por si los datos se vuelven a poner desde otro sitio)
                if (map.getLayer(detailLayerId)) {
                    map.setLayoutProperty(detailLayerId, "visibility", "none");
                }
                return;
            }

            // Si no hay posts, limpiamos
            if (!postsInCity || !postsInCity.length) {
                map.getSource(detailSourceId)?.setData({
                    type: "FeatureCollection",
                    features: [],
                });
                if (map.getLayer(detailLayerId)) {
                    map.setLayoutProperty(detailLayerId, "visibility", "none");
                }
                return;
            }

            // Si llegamos aquí, mostramos detalle: asegurarnos que la capa está visible
            if (map.getLayer(detailLayerId)) {
                map.setLayoutProperty(detailLayerId, "visibility", "visible");
            }

            const features = postsInCity
                .filter((loc) => loc.latitude && loc.longitude)
                .map((loc) => {
                    const poly = createGeoJSONCircle(
                        [parseFloat(loc.longitude), parseFloat(loc.latitude)],
                        radiusMeters,
                        64,
                    );
                    poly.properties = { id: loc.id };
                    return poly;
                });

            map.getSource(detailSourceId).setData({
                type: "FeatureCollection",
                features,
            });
        }

        function syncMapPresentation({ respectForceDetail = true } = {}) {
            const zoom = map.getZoom();

            if (respectForceDetail && forceDetailMode) {
                summaryVisible = false;
                summaryLayers.forEach((layer) =>
                    map.setLayoutProperty(layer, "visibility", "none"),
                );
                showDetailCircles(locations);
                return;
            }

            if (zoom >= 9) {
                summaryVisible = false;
                summaryLayers.forEach((layer) =>
                    map.setLayoutProperty(layer, "visibility", "none"),
                );
                showDetailCircles(locations);
                return;
            }

            summaryVisible = true;
            summaryLayers.forEach((layer) =>
                map.setLayoutProperty(layer, "visibility", "visible"),
            );
            map.getSource(detailSourceId)?.setData({
                type: "FeatureCollection",
                features: [],
            });
            if (map.getLayer(detailLayerId)) {
                map.setLayoutProperty(detailLayerId, "visibility", "none");
            }
        }

        // ---------------------
        // ZOOM AUTOMÁTICO
        // ---------------------
        // Lógica para zoom manual
        map.on(
            "zoom",
            throttle(() => {
                syncMapPresentation();
            }, 100),
        );

        let formMarker = null; // marker de nueva selección
        let originalMarker = null; // marker fijo de la ubicación original

        const hideDetailLayer = () => {
            if (map.getSource(detailSourceId)) {
                map.getSource(detailSourceId).setData({
                    type: "FeatureCollection",
                    features: [],
                });
            }
            if (map.getLayer(detailLayerId)) {
                map.setLayoutProperty(detailLayerId, "visibility", "none");
            }
        };

        const handleAddFormOpened = () => {
            mapState.showAddForm = true;
            mapState.showEditForm = false;
            hideDetailLayer();
        };

        const handleCloseForms = () => {
            mapState.showAddForm = false;
            mapState.showEditForm = false;
            clearMarkers();
        };

        const handleMapExited = () => {
            debouncedClearPinnedPost.cancel();

            if (currentMarker) {
                currentMarker.remove();
                currentMarker = null;
            }
        };

        const handlePostLoaded = (event) => {
            const post = Array.isArray(event.detail)
                ? event.detail[0]
                : event.detail;

            const lat = parseFloat(post.lat);
            const lng = parseFloat(post.lng);

            let delivery = post.delivery;
            if (Array.isArray(delivery)) {
                delivery = String(delivery[0]);
            } else {
                delivery = String(delivery);
            }

            mapState.currentDelivery = delivery;
            window.editingPostLocation = { lat, lng };

            map.flyTo({ center: [lng, lat], zoom: 12, essential: true });

            if (originalMarker) originalMarker.remove();
            originalMarker = new mapboxgl.Marker({ color: "#E65100" })
                .setLngLat([lng, lat])
                .addTo(map);

            mapState.showAddForm = false;
            mapState.showEditForm = delivery === "1" || delivery === "3";

            if (!mapState.showEditForm) {
                showDetailCircles(locations);
            } else {
                hideDetailLayer();
            }
        };

        registerWindowEvent("add-form-opened", handleAddFormOpened);
        registerWindowEvent("closeAddPostForm", handleCloseForms);
        registerWindowEvent("closeEditPostForm", handleCloseForms);
        registerWindowEvent("post-loaded", handlePostLoaded);
        registerWindowEvent("dollo-map-exited", handleMapExited);

        function clearMarkers() {
            if (formMarker) {
                formMarker.remove();
                formMarker = null;
            }
            if (originalMarker) {
                originalMarker.remove();
                originalMarker = null;
            }
            if (currentMarker) {
                currentMarker.remove();
                currentMarker = null;
            }

            mapState.showAddForm = false;
            mapState.showEditForm = false;
            mapState.currentDelivery = null;

            // mostrar detalle completo del mapa
            showDetailCircles(locations);
            summaryVisible = false;

            Livewire.dispatch("updateLocationFromMap");
        }

        // === Modalidad cambia (1=presencial, 2=online, 3=ambos) ===
        registerWindowEvent("modalidad-changed", (e) => {
            mapState.currentDelivery = e.detail; // 1=presencial, 2=online, 3=ambos

            if (mapState.currentDelivery === "2") {
                // Online → ocultar todos los markers
                if (formMarker) {
                    formMarker.remove();
                    formMarker = null;
                }
                if (originalMarker) {
                    originalMarker.remove();
                    originalMarker = null;
                }
                if (currentMarker) {
                    currentMarker.remove();
                    currentMarker = null;
                }

                Livewire.dispatch("updateLocationFromMap");
            } else if (
                mapState.currentDelivery === "1" ||
                mapState.currentDelivery === "3"
            ) {
                // Presencial o ambos → restaurar marker original si estamos en edit form

                if (
                    mapState.showEditForm &&
                    !originalMarker &&
                    window.editingPostLocation
                ) {
                    const { lat, lng } = window.editingPostLocation;
                    if (lat && lng) {
                        originalMarker = new mapboxgl.Marker({
                            color: "#E65100",
                        })
                            .setLngLat([lng, lat])
                            .addTo(map);

                        map.flyTo({
                            center: [lng, lat],
                            zoom: 12,
                            essential: true,
                        });
                    }
                }
            }
        });

        // ---------------------
        // INICIALIZAR
        // ---------------------
        updateSummaryFromLocations();
    });
};

document.addEventListener("DOMContentLoaded", initDolloMainMap);
window.addEventListener("dollo-map-mount", initDolloMainMap);

window.renderPinnedMap = (containerId, coords) => {
    if (!containerId) return;
    const defaults = { lat: 40.4168, lng: -3.7038 };
    const hasCoords = coords && coords.lat != null && coords.lng != null;
    const lat = hasCoords ? coords.lat : defaults.lat;
    const lng = hasCoords ? coords.lng : defaults.lng;

    const mount = async () => {
        const el = document.getElementById(containerId);
        if (!el) return;
        if (el.dataset.mapReady) {
            if (el._mapboxMap) {
                requestAnimationFrame(() => el._mapboxMap.resize());
            }
            return;
        }

        const mapboxgl = await ensureMapboxReady();
        if (!mapboxgl) {
            return;
        }

        if (el.dataset.mapReady) {
            if (el._mapboxMap) {
                requestAnimationFrame(() => el._mapboxMap.resize());
            }
            return;
        }

        el.dataset.mapReady = "1";

        const map = new mapboxgl.Map({
            container: containerId,
            style: "mapbox://styles/mapbox/streets-v12",
            center: [parseFloat(lng), parseFloat(lat)],
            zoom: 13,
            cooperativeGestures: true,
        });
        el._mapboxMap = map;

        map.addControl(
            new mapboxgl.NavigationControl({ showCompass: false }),
            "top-right",
        );

        let selectionMarker = null;
        const geocodeSelection = async (lngSel, latSel) => {
            try {
                const response = await fetch(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${lngSel},${latSel}.json?access_token=${mapboxgl.accessToken}`,
                );
                const payload = await response.json();
                let address = "";
                let city = "";
                let province = "";
                let region = "";
                let postalCode = "";
                if (payload.features && payload.features.length > 0) {
                    const place = payload.features[0];
                    address = place.place_name ?? "";
                    place.context?.forEach((c) => {
                        if (c.id.startsWith("place")) city = c.text;
                        if (c.id.startsWith("region")) region = c.text;
                        if (c.id.startsWith("postcode")) postalCode = c.text;
                        if (
                            c.id.startsWith("district") ||
                            c.id.startsWith("province")
                        ) {
                            province = c.text;
                        }
                    });
                }

                Livewire.dispatch("updateLocationFromMap", {
                    lat: latSel,
                    lng: lngSel,
                    address,
                    city,
                    province,
                    region,
                    postalCode,
                });
            } catch (err) {
                console.error("Geocoding error (pinned map):", err);
            }
        };

        map.on("load", () => {
            if (hasCoords) {
                new mapboxgl.Marker({ color: "#E65100" })
                    .setLngLat([parseFloat(lng), parseFloat(lat)])
                    .addTo(map);
            }

            map.on("click", async (e) => {
                const clickLng = e.lngLat.lng;
                const clickLat = e.lngLat.lat;

                if (!selectionMarker) {
                    selectionMarker = new mapboxgl.Marker({ color: "#3FB1CE" })
                        .setLngLat([clickLng, clickLat])
                        .addTo(map);
                } else {
                    selectionMarker.setLngLat([clickLng, clickLat]);
                }

                await geocodeSelection(clickLng, clickLat);
            });
        });

        const resize = () => map.resize();
        setTimeout(resize, 150);
        setTimeout(resize, 400);
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", mount, { once: true });
    } else {
        requestAnimationFrame(mount);
    }
};

window.refreshPinnedMap = (containerId) => {
    const el = document.getElementById(containerId);
    if (!el || !el._mapboxMap) return;
    requestAnimationFrame(() => {
        el._mapboxMap.resize();
    });
};

// ---------------------
// ACTIVE LINK SCROLL
// ---------------------
const sections = document.querySelectorAll("section[id]");
window.addEventListener("scroll", () => {
    const scrollY = window.pageYOffset;
    sections.forEach((section) => {
        const top = section.offsetTop - 75;
        const bottom = top + section.offsetHeight;
        const sectionId = section.getAttribute("id");
        const link = document.querySelector(`.nav a[href*="${sectionId}"]`);
        if (!link) return;
        if (scrollY > top && scrollY <= bottom) link.classList.add("active");
        else link.classList.remove("active");
    });
});
